import { useState, createContext, useEffect, useMemo } from 'react'

const VideoStreamingContext = createContext()

const STORAGE_KEY = 'videoStreamingState'

// Helper to get initial state from localStorage
const getInitialState = () => {
  if (typeof window === 'undefined') {
    return {
      isStreaming: false,
      isFeedPublished: false,
      isMuted: true,
      streamId: null
    }
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        isStreaming: parsed.isStreaming ?? false,
        isFeedPublished: parsed.isFeedPublished ?? false,
        isMuted: parsed.isMuted ?? true,
        streamId: parsed.streamId ?? null
      }
    }
  } catch (error) {
    console.error(
      'Error loading video streaming state from localStorage:',
      error
    )
  }

  return {
    isStreaming: false,
    isFeedPublished: false,
    isMuted: true,
    streamId: null
  }
}

export const VideoStreamingProvider = ({ children }) => {
  const initialState = getInitialState()
  const [isStreaming, setIsStreaming] = useState(initialState.isStreaming)
  const [isFeedPublished, setIsFeedPublished] = useState(
    initialState.isFeedPublished
  )
  const [isMuted, setIsMuted] = useState(initialState.isMuted)
  const [streamId, setStreamId] = useState(initialState.streamId)
  const [isHydrated, setIsHydrated] = useState(false)

  // Load from localStorage after component mounts (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          setIsStreaming(parsed.isStreaming ?? false)
          setIsFeedPublished(parsed.isFeedPublished ?? false)
          setIsMuted(parsed.isMuted ?? true)
          setStreamId(parsed.streamId ?? null)
        }
      } catch (error) {
        console.error(
          'Error loading video streaming state from localStorage:',
          error
        )
      }
      setIsHydrated(true)
    }
  }, [])

  // Save to localStorage whenever state changes (but only after hydration)
  useEffect(() => {
    if (typeof window !== 'undefined' && isHydrated) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            isStreaming,
            isFeedPublished,
            isMuted,
            streamId
          })
        )
      } catch (error) {
        console.error(
          'Error saving video streaming state to localStorage:',
          error
        )
      }
    }
  }, [isStreaming, isFeedPublished, isMuted, streamId, isHydrated])

  const value = useMemo(
    () => ({
      isStreaming,
      setIsStreaming,
      isMuted,
      setIsMuted,
      isFeedPublished,
      setIsFeedPublished,
      streamId,
      setStreamId
    }),
    [
      isStreaming,
      setIsStreaming,
      isMuted,
      setIsMuted,
      isFeedPublished,
      setIsFeedPublished,
      streamId,
      setStreamId
    ]
  )

  return (
    <VideoStreamingContext.Provider value={value}>
      {children}
    </VideoStreamingContext.Provider>
  )
}

export default VideoStreamingContext
